// src/components/map/sidebar/TechnicianSidebar.tsx
import React from 'react';
import { Drawer, IconButton, Box, Button } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { alertSuccess, alertError } from '../../../alertDispatcher';
import { DumpResult, printMissionCores, printMissionSpecs, printMissionZones } from '../../../__test__/helpers';
import { getCurrentMission } from '../../../dataModelHelpers';

type TechnicianSidebarProps = {
  open: boolean;
  onClose: () => void;
};

type DebugDumpRowProps = {
  label: string;
  dump: () => DumpResult;
};

const DebugDumpRow: React.FC<DebugDumpRowProps> = ({ label, dump }) => {
  const handleCopy = async () => {
    try {
      const { json } = dump();
      const payload = JSON.stringify(json, null, 2);

      await navigator.clipboard.writeText(payload);
      alertSuccess(`Copied ${label} to clipboard`);
    } catch (e) {
      alertError(`Failed to copy ${label}: ${(e as Error).message}`);
    }
  };

  // Console output works only in dev — production strips console.* calls
  // (see vite.config.mts esbuild.drop). The helper still runs and returns
  // a result but no DevTools output appears in prod.
  const handleConsole = () => {
    dump();
  };

  return (
    <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
      <Box flex={1}>{label}</Box>
      <Box display="flex" gap={8}>
        <Button variant="outlined" size="small" onClick={handleCopy}>
          Copy
        </Button>
        <Button variant="outlined" size="small" onClick={handleConsole}>
          Console
        </Button>
      </Box>
    </Box>
  );
};

const TechnicianSidebar: React.FC<TechnicianSidebarProps> = ({ open, onClose }) => {
  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      variant="persistent"
      PaperProps={{
        style: {
          width: 450,
          borderTopLeftRadius: 8,
          borderBottomLeftRadius: 8,
          top: 80,
        },
      }}
      ModalProps={{
        keepMounted: true,
        disableEnforceFocus: true,
        hideBackdrop: true,
      }}
    >
      <Box display="flex" alignItems="center" justifyContent="space-between" p={2}>
        <h3>Technician Tools</h3>
        <IconButton onClick={onClose} size="large">
          <CloseIcon />
        </IconButton>
      </Box>
      <Box p={2}>
        <DebugDumpRow label="Mission Cores" dump={() => printMissionCores()} />
        <DebugDumpRow label="Mission Specs" dump={() => printMissionSpecs()} />
        <DebugDumpRow label="Mission Zones" dump={() => printMissionZones()} />
        <DebugDumpRow
          label="Mission To ROS"
          dump={() => {
            const mission = getCurrentMission();

            if (!mission) {
              return {
                text: '',
                json: {},
              };
            }

            const msg = mission.to_ros_msg();
            console.log(msg);

            return {
              text: JSON.stringify(msg) || '',
              json: msg || {},
            };
          }}
        />
      </Box>
    </Drawer>
  );
};

export default TechnicianSidebar;
